import React, { useEffect, useMemo, useState } from 'react';
import { adminService } from '../services/authService';

interface LocalizationTranslation {
  id: string;
  key: string;
  language: string;
  text: string;
  status: string;
  source: string;
  reviewer?: string;
}

interface LocalizationIssue {
  id: string;
  key: string;
  language: string;
  platform?: string;
  fallbackValue?: string;
  occurrences: number;
  lastReportedAt: string;
  appVersion?: string;
  translation?: LocalizationTranslation | null;
}

const LANGUAGE_OPTIONS = [
  { code: 'tr', label: 'Türkçe' },
  { code: 'en', label: 'İngilizce' },
  { code: 'de', label: 'Almanca' },
  { code: 'ar', label: 'Arapça' },
  { code: 'zh', label: 'Çince' },
  { code: 'hi', label: 'Hintçe' },
  { code: 'es', label: 'İspanyolca' },
  { code: 'ja', label: 'Japonca' },
];

const PLATFORM_OPTIONS = [
  { value: '', label: 'Tümü' },
  { value: 'android', label: 'Android' },
  { value: 'iOS', label: 'iOS' },
  { value: 'macOS', label: 'macOS' },
  { value: 'linux', label: 'Linux' },
  { value: 'windows', label: 'Windows' },
  { value: 'fuchsia', label: 'Fuchsia' },
  { value: 'unknown', label: 'Bilinmiyor' },
];

const LocalizationsPage: React.FC = () => {
  const [language, setLanguage] = useState('tr');
  const [platform, setPlatform] = useState('');
  const [issues, setIssues] = useState<LocalizationIssue[]>([]);
  const [loading, setLoading] = useState(false);
  const [autoLoading, setAutoLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [showAllTranslations, setShowAllTranslations] = useState(false);

  const selectedLanguageLabel = useMemo(() => {
    return LANGUAGE_OPTIONS.find(option => option.code === language)?.label || language;
  }, [language]);

  const fetchIssues = async () => {
    setLoading(true);
    setMessage(null);
    setError(null);
    try {
      const response = await adminService.getLocalizationIssues({
        language,
        includeTranslated: showAllTranslations,
        platform: platform || undefined,
      });
      const data: LocalizationIssue[] = response.issues || [];
      setIssues(data);
      const defaults: Record<string, string> = {};
      data.forEach(issue => {
        defaults[issue.id] = issue.translation?.text || '';
      });
      setEditValues(defaults);
    } catch (err: any) {
      setError(err?.message || 'Çeviri listesi alınamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIssues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, platform, showAllTranslations]);

  const handleInputChange = (issueId: string, value: string) => {
    setEditValues(prev => ({
      ...prev,
      [issueId]: value,
    }));
  };

  const handleSave = async (issue: LocalizationIssue) => {
    setMessage(null);
    setError(null);
    const text = editValues[issue.id]?.trim();
    if (!text) {
      setError('Çeviri metni boş olamaz.');
      return;
    }

    try {
      await adminService.saveLocalizationTranslation({
        key: issue.key,
        language: issue.language,
        text,
        status: 'APPROVED',
        source: 'MANUAL',
      });
      setMessage('Çeviri kaydedildi.');
      fetchIssues();
    } catch (err: any) {
      setError(err?.message || 'Çeviri kaydedilemedi');
    }
  };

  const handleAutoTranslate = async () => {
    setAutoLoading(true);
    setMessage(null);
    setError(null);
    try {
      const result = await adminService.autoTranslateLocalizations({
        language,
        limit: 20,
      });
      if (result.translated > 0) {
        setMessage(`${result.translated} anahtar için AI önerisi üretildi.`);
      } else {
        setMessage('AI önerisi üretilemedi veya eksik anahtar bulunamadı.');
      }
      fetchIssues();
    } catch (err: any) {
      setError(err?.message || 'AI çeviri önerisi başarısız oldu');
    } finally {
      setAutoLoading(false);
    }
  };

  const renderStatusBadge = (issue: LocalizationIssue) => {
    if (!issue.translation) {
      return (
        <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
          Eksik
        </span>
      );
    }

    const status = issue.translation.status;
    const source = issue.translation.source;
    const color =
      status === 'APPROVED'
        ? 'bg-green-100 text-green-800'
        : status === 'PENDING'
          ? 'bg-yellow-100 text-yellow-800'
          : 'bg-gray-100 text-gray-800';

    return (
      <div className="flex flex-col gap-1">
        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${color}`}>
          {status}
        </span>
        <span className="text-xs text-gray-500">
          Kaynak: {source}
        </span>
      </div>
    );
  };

  return (
    <div>
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Çeviri Yönetimi</h1>
          <p className="mt-1 text-sm text-gray-500">
            Eksik veya güncel olmayan metinleri yönetin, AI önerileri oluşturun.
          </p>
        </div>

        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Dil</label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              {LANGUAGE_OPTIONS.map(option => (
                <option key={option.code} value={option.code}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">Platform</label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
            >
              {PLATFORM_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2 pt-4 md:pt-0">
            <input
              id="toggle-all"
              type="checkbox"
              checked={showAllTranslations}
              onChange={(e) => setShowAllTranslations(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label htmlFor="toggle-all" className="text-sm text-gray-600">
              Çevrilmiş olanları da göster
            </label>
          </div>

          <button
            onClick={handleAutoTranslate}
            disabled={autoLoading}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:opacity-60"
          >
            {autoLoading ? 'AI Önerileri Üretiliyor...' : 'AI Önerisi Üret'}
          </button>
        </div>
      </div>

      {message && (
        <div className="mb-4 rounded-md bg-green-50 p-4 text-sm text-green-800">
          {message}
        </div>
      )}
      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Anahtar
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Varsayılan Metin
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Platform
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Son Gönderim
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Çeviri
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Durum
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Aksiyon
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {loading && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-sm text-gray-500">
                    Yükleniyor...
                  </td>
                </tr>
              )}
              {!loading && issues.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-sm text-gray-500">
                    Bu filtrelere göre eksik çeviri bulunamadı.
                  </td>
                </tr>
              )}
              {!loading && issues.map(issue => (
                <tr key={issue.id}>
                  <td className="px-3 py-4 text-sm font-medium text-gray-900">{issue.key}</td>
                  <td className="px-3 py-4 text-sm text-gray-700">
                    <div className="max-w-xs truncate" title={issue.fallbackValue || '-'}>
                      {issue.fallbackValue || '-'}
                    </div>
                    <div className="text-xs text-gray-400">
                      {issue.occurrences} hata
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    {issue.platform || '—'}
                    {issue.appVersion && (
                      <div className="text-xs text-gray-400">v{issue.appVersion}</div>
                    )}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-500">
                    {new Date(issue.lastReportedAt).toLocaleString('tr-TR')}
                  </td>
                  <td className="px-3 py-4 text-sm text-gray-700">
                    <textarea
                      value={editValues[issue.id] ?? ''}
                      onChange={(e) => handleInputChange(issue.id, e.target.value)}
                      rows={3}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500"
                      placeholder={`${selectedLanguageLabel} çevirisini girin...`}
                    />
                  </td>
                  <td className="px-3 py-4 text-sm">
                    {renderStatusBadge(issue)}
                  </td>
                  <td className="px-3 py-4 text-sm">
                    <button
                      onClick={() => handleSave(issue)}
                      className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-primary-700"
                    >
                      Kaydet
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LocalizationsPage;

